/* ============================================================
   Vizalone theme - JavaScript
   One file, loaded with `defer` from layout/theme.liquid.
   Sections:
     1. Helpers (money, toast, focus)
     2. Header, mobile menu, search
     3. Cart drawer + AJAX add to cart (Section Rendering API)
     4. Cart line changes (drawer + cart page)
     5. Product page: gallery, variants, quantity, sticky bar
     6. Accordions, collection sort, scroll animations
   ============================================================ */
(function () {
  'use strict';

  var theme = window.theme || {};
  var routes = window.routes || {};
  var strings = window.cartStrings || {};
  var productStrings = window.productStrings || {};
  var reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------
     1. Helpers
     --------------------------------------------------------- */

  /* Format cents using the shop's own money format (Settings > General > Store currency).
     Falls back to euro formatting when the format is missing. */
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    format = format || theme.moneyFormat || '€{{amount_with_comma_separator}}';
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;

    function withDelimiters(number, precision, thousands, decimal) {
      precision = precision == null ? 2 : precision;
      thousands = thousands || ',';
      decimal = decimal || '.';
      if (isNaN(number) || number == null) return '0';
      var fixed = (number / 100.0).toFixed(precision);
      var parts = fixed.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var centsPart = parts[1] ? decimal + parts[1] : '';
      return dollars + centsPart;
    }

    var match = format.match(placeholder);
    var value = '';
    switch (match ? match[1] : 'amount') {
      case 'amount': value = withDelimiters(cents, 2); break;
      case 'amount_no_decimals': value = withDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = withDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = withDelimiters(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = withDelimiters(cents, 2, "'", '.'); break;
      case 'amount_with_space_separator': value = withDelimiters(cents, 2, ' ', ','); break;
      default: value = withDelimiters(cents, 2);
    }
    return format.replace(placeholder, value);
  }

  function showToast(message) {
    var toast = document.querySelector('.cart-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'cart-toast';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      document.body.appendChild(toast);
    }
    toast.textContent = message;
    requestAnimationFrame(function () { toast.classList.add('is-visible'); });
    clearTimeout(toast._timer);
    toast._timer = setTimeout(function () { toast.classList.remove('is-visible'); }, 2800);
  }

  function updateCartCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach(function (bubble) {
      bubble.textContent = count;
      if (bubble.classList.contains('cart-count-bubble')) {
        bubble.hidden = !(count > 0);
      }
    });
  }

  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  /* Keep keyboard focus inside an open drawer */
  function trapFocus(container, event) {
    if (event.key !== 'Tab') return;
    var items = Array.prototype.filter.call(container.querySelectorAll(FOCUSABLE), function (el) {
      return el.offsetParent !== null;
    });
    if (!items.length) return;
    var first = items[0];
    var last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  }

  /* ---------------------------------------------------------
     2. Header, mobile menu, search
     --------------------------------------------------------- */
  function initHeader() {
    var header = document.querySelector('[data-header]');
    if (!header) return;
    var isTransparent = header.getAttribute('data-transparent') === 'true'
      && document.body.classList.contains('template-index');
    if (!isTransparent) return;

    function onScroll() {
      header.classList.toggle('is-top', window.scrollY <= 40);
    }
    document.body.classList.add('has-transparent-header');
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  var openDrawer = null; /* { el, close } of the drawer that is currently open */

  function initMobileMenu() {
    var toggles = document.querySelectorAll('[data-menu-toggle]');
    var nav = document.querySelector('[data-mobile-nav]');
    var overlay = document.querySelector('[data-drawer-overlay]');
    if (!nav) return;
    var opener = null;

    function setExpanded(value) {
      toggles.forEach(function (t) { if (t.hasAttribute('aria-expanded')) t.setAttribute('aria-expanded', String(value)); });
    }
    function open() {
      opener = document.activeElement;
      nav.classList.add('is-open');
      nav.removeAttribute('inert');
      nav.setAttribute('aria-hidden', 'false');
      if (overlay) overlay.classList.add('is-visible');
      document.body.classList.add('drawer-open');
      setExpanded(true);
      var closeBtn = nav.querySelector('.mobile-nav__close');
      if (closeBtn) closeBtn.focus();
      openDrawer = { el: nav, close: close };
    }
    function close() {
      nav.classList.remove('is-open');
      nav.setAttribute('inert', '');
      nav.setAttribute('aria-hidden', 'true');
      if (overlay) overlay.classList.remove('is-visible');
      document.body.classList.remove('drawer-open');
      setExpanded(false);
      if (opener && opener.focus) opener.focus();
      openDrawer = null;
    }
    toggles.forEach(function (t) {
      t.addEventListener('click', function () {
        if (nav.classList.contains('is-open')) close(); else open();
      });
    });
    if (overlay) overlay.addEventListener('click', function () { if (nav.classList.contains('is-open')) close(); });
    nav.addEventListener('keydown', function (e) { trapFocus(nav, e); });
  }

  function initSearch() {
    var toggle = document.querySelector('[data-search-toggle]');
    var panel = document.querySelector('[data-search-panel]');
    var closeBtn = document.querySelector('[data-search-close]');
    if (!toggle || !panel) return;

    var input = panel.querySelector('input[name="q"]');
    var results = panel.querySelector('[data-search-results]');
    var timer = null;
    var controller = null;

    var chevron = '<svg class="search-panel__chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="9 6 15 12 9 18"></polyline></svg>';

    function openPanel() {
      panel.classList.add('is-open');
      panel.removeAttribute('inert');
      panel.setAttribute('aria-hidden', 'false');
      toggle.setAttribute('aria-expanded', 'true');
      if (input) input.focus();
    }
    function closePanel() {
      panel.classList.remove('is-open');
      panel.setAttribute('inert', '');
      panel.setAttribute('aria-hidden', 'true');
      toggle.setAttribute('aria-expanded', 'false');
      clearResults();
    }
    function clearResults() {
      if (controller) controller.abort();
      if (results) { results.hidden = true; results.innerHTML = ''; }
    }
    function esc(str) {
      var d = document.createElement('div');
      d.textContent = str == null ? '' : String(str);
      return d.innerHTML;
    }
    function formatPrice(value) {
      var n = parseFloat(value);
      if (isNaN(n)) return '';
      return formatMoney(Math.round(n * 100));
    }
    function sized(url, w) {
      if (!url) return '';
      return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + w;
    }
    function render(products, term) {
      if (!results) return;
      if (!products.length) {
        results.innerHTML = '<p class="search-panel__empty">' + esc(theme.strings && theme.strings.noResults || 'No results found.') + '</p>';
        results.hidden = false;
        return;
      }
      var html = '<ul class="search-panel__list" role="list">';
      products.forEach(function (p) {
        var img = p.image || (p.featured_image && p.featured_image.url) || '';
        html += '<li><a class="search-panel__item" href="' + esc(p.url) + '">' +
          (img
            ? '<img class="search-panel__thumb" src="' + esc(sized(img, 200)) + '" alt="" loading="lazy" width="80" height="80">'
            : '<span class="search-panel__thumb"></span>') +
          '<span class="search-panel__info">' +
            '<span class="search-panel__name">' + esc(p.title) + '</span>' +
            '<span class="search-panel__sub">' + esc(formatPrice(p.price)) + '</span>' +
          '</span>' + chevron + '</a></li>';
      });
      html += '</ul>';
      var searchUrl = new URL(routes.search_url || '/search', window.location.origin);
      searchUrl.searchParams.set('q', term);
      html += '<a class="search-panel__all" href="' + esc(searchUrl.pathname + searchUrl.search) + '">' + esc(theme.strings && theme.strings.viewAllResults || 'View all results') + '</a>';
      results.innerHTML = html;
      results.hidden = false;
    }
    function fetchSuggestions(term) {
      if (controller) controller.abort();
      controller = new AbortController();
      var url = new URL((routes.predictive_search_url || '/search/suggest') + '.json', window.location.origin);
      url.searchParams.set('q', term);
      url.searchParams.set('resources[type]', 'product');
      url.searchParams.set('resources[limit]', '6');
      fetch(url.toString(), { signal: controller.signal })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          var products = (data && data.resources && data.resources.results && data.resources.results.products) || [];
          render(products, term);
        })
        .catch(function (err) { if (err.name !== 'AbortError') clearResults(); });
    }

    panel.setAttribute('inert', '');
    toggle.addEventListener('click', function () {
      if (panel.classList.contains('is-open')) closePanel(); else openPanel();
    });
    if (closeBtn) closeBtn.addEventListener('click', function () { closePanel(); toggle.focus(); });
    if (input) {
      input.addEventListener('input', function () {
        var term = input.value.trim();
        clearTimeout(timer);
        if (term.length < 2) { clearResults(); return; }
        timer = setTimeout(function () { fetchSuggestions(term); }, 250);
      });
    }
    panel.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { closePanel(); toggle.focus(); }
    });
  }

  /* ---------------------------------------------------------
     3. Cart drawer + AJAX add to cart
     --------------------------------------------------------- */
  var drawerOpener = null;

  function getDrawer() { return document.querySelector('[data-cart-drawer]'); }

  function openCartDrawer() {
    var drawer = getDrawer();
    var overlay = document.querySelector('[data-drawer-overlay]');
    if (!drawer) { window.location.href = routes.cart_url || '/cart'; return; }
    if (!drawer.classList.contains('is-open')) drawerOpener = document.activeElement;
    drawer.removeAttribute('inert');
    drawer.classList.add('is-open');
    if (overlay) overlay.classList.add('is-visible');
    document.body.classList.add('drawer-open');
    openDrawer = { el: drawer, close: closeCartDrawer };
    var closeBtn = drawer.querySelector('[data-cart-close]');
    setTimeout(function () { if (closeBtn) closeBtn.focus(); }, 50);
  }

  function closeCartDrawer() {
    var drawer = getDrawer();
    var overlay = document.querySelector('[data-drawer-overlay]');
    if (!drawer || !drawer.classList.contains('is-open')) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('inert', '');
    if (overlay) overlay.classList.remove('is-visible');
    document.body.classList.remove('drawer-open');
    openDrawer = null;
    if (drawerOpener && drawerOpener.focus) drawerOpener.focus();
  }

  /* Replace the drawer body with fresh HTML rendered by Shopify */
  function applyDrawerHtml(sectionHtml) {
    if (!sectionHtml) return;
    var doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
    var freshBody = doc.querySelector('[data-cart-drawer-body]');
    var body = document.querySelector('[data-cart-drawer-body]');
    if (!freshBody || !body) return;
    body.innerHTML = freshBody.innerHTML;
    var count = parseInt(freshBody.getAttribute('data-item-count'), 10) || 0;
    body.setAttribute('data-item-count', count);
    updateCartCount(count);
  }

  function sectionsUrl() {
    return (routes.root_url || '/').replace(/\/?$/, '/');
  }

  function refreshCartDrawer() {
    var url = new URL(sectionsUrl(), window.location.origin);
    url.searchParams.set('sections', 'cart-drawer');
    return fetch(url.toString())
      .then(function (r) { return r.json(); })
      .then(function (data) { applyDrawerHtml(data && data['cart-drawer']); })
      .catch(function () { /* drawer keeps old content; page reload will fix it */ });
  }

  function initCartDrawer() {
    document.querySelectorAll('[data-cart-toggle]').forEach(function (toggle) {
      toggle.addEventListener('click', function (e) {
        if (document.body.classList.contains('template-cart')) return; /* on the cart page: follow the link */
        e.preventDefault();
        openCartDrawer();
      });
    });

    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-cart-close]')) closeCartDrawer();
    });

    var overlay = document.querySelector('[data-drawer-overlay]');
    if (overlay) overlay.addEventListener('click', closeCartDrawer);

    var drawer = getDrawer();
    if (drawer) drawer.addEventListener('keydown', function (e) { trapFocus(drawer, e); });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && openDrawer) openDrawer.close();
    });
  }

  function initAjaxAddToCart() {
    document.addEventListener('submit', function (e) {
      var form = e.target.closest('[data-product-form], form.product-form');
      if (!form) return;
      var idInput = form.querySelector('[name="id"]');
      if (!idInput) return;

      e.preventDefault();
      var buttons = Array.prototype.slice.call(form.querySelectorAll('[type="submit"]'));
      if (form.id) {
        buttons = buttons.concat(Array.prototype.slice.call(document.querySelectorAll('[type="submit"][form="' + form.id + '"]')));
      }
      buttons.forEach(function (b) { b.disabled = true; b.classList.add('is-loading'); b.setAttribute('aria-busy', 'true'); });

      var formData = new FormData(form);
      formData.append('sections', 'cart-drawer');
      formData.append('sections_url', window.location.pathname);

      fetch((routes.cart_add_url || '/cart/add') + '.js', {
        method: 'POST',
        headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData
      })
        .then(function (response) {
          return response.json().then(function (data) { return { ok: response.ok, data: data }; });
        })
        .then(function (result) {
          if (!result.ok) {
            showToast(result.data.description || result.data.message || strings.error || 'Something went wrong.');
            return;
          }
          if (result.data.sections && result.data.sections['cart-drawer']) {
            applyDrawerHtml(result.data.sections['cart-drawer']);
          } else {
            refreshCartDrawer();
          }
          openCartDrawer();
        })
        .catch(function () { showToast(strings.error || 'Something went wrong.'); })
        .finally(function () {
          buttons.forEach(function (b) {
            b.classList.remove('is-loading');
            b.removeAttribute('aria-busy');
            b.disabled = b.hasAttribute('data-unavailable');
          });
        });
    });
  }

  /* ---------------------------------------------------------
     4. Cart line changes (drawer + cart page)
     --------------------------------------------------------- */
  var cartBusy = false;

  function cartPageSection() {
    var page = document.querySelector('[data-cart-page]');
    if (!page) return null;
    var wrapper = page.closest('.shopify-section');
    return wrapper ? { el: wrapper, id: wrapper.id.replace('shopify-section-', '') } : null;
  }

  function changeCartLine(key, quantity) {
    if (cartBusy) return;
    cartBusy = true;
    var sections = ['cart-drawer'];
    var pageSection = cartPageSection();
    if (pageSection) sections.push(pageSection.id);

    document.querySelectorAll('[data-cart-item][data-key="' + key + '"]').forEach(function (row) { row.classList.add('is-updating'); });

    fetch((routes.cart_change_url || '/cart/change') + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity, sections: sections, sections_url: window.location.pathname })
    })
      .then(function (r) { return r.json().then(function (data) { return { ok: r.ok, data: data }; }); })
      .then(function (result) {
        if (!result.ok) {
          showToast(result.data.description || result.data.message || strings.error || 'Something went wrong.');
          window.location.reload();
          return;
        }
        var data = result.data;
        updateCartCount(data.item_count);
        if (data.sections) {
          applyDrawerHtml(data.sections['cart-drawer']);
          if (pageSection && data.sections[pageSection.id]) {
            var doc = new DOMParser().parseFromString(data.sections[pageSection.id], 'text/html');
            var fresh = doc.querySelector('.shopify-section') || doc.body;
            pageSection.el.innerHTML = fresh.innerHTML;
          }
        }
      })
      .catch(function () { showToast(strings.error || 'Something went wrong.'); })
      .finally(function () { cartBusy = false; });
  }

  function initCartInteractions() {
    document.addEventListener('click', function (e) {
      var minus = e.target.closest('[data-cart-minus]');
      var plus = e.target.closest('[data-cart-plus]');
      var remove = e.target.closest('[data-cart-remove]');
      var trigger = minus || plus || remove;
      if (!trigger) return;
      e.preventDefault();

      var key = trigger.getAttribute('data-key');
      if (remove) { changeCartLine(key, 0); return; }

      var row = trigger.closest('[data-cart-item]');
      var input = row ? row.querySelector('[data-cart-quantity]') : null;
      if (!input) return;
      var val = parseInt(input.value, 10) || 1;
      val = minus ? Math.max(0, val - 1) : val + 1;
      input.value = val;
      changeCartLine(key, val);
    });

    document.addEventListener('change', function (e) {
      var input = e.target.closest('[data-cart-quantity]');
      if (!input) return;
      var val = parseInt(input.value, 10);
      if (isNaN(val) || val < 0) val = 0;
      changeCartLine(input.getAttribute('data-key'), val);
    });
  }

  /* ---------------------------------------------------------
     5. Product page
     --------------------------------------------------------- */
  function initProductGallery() {
    document.querySelectorAll('[data-product-gallery]').forEach(function (gallery) {
      var track = gallery.querySelector('[data-gallery-track]');
      if (!track) return;
      var slides = Array.prototype.slice.call(track.querySelectorAll('[data-gallery-slide]'));
      var thumbs = Array.prototype.slice.call(gallery.querySelectorAll('[data-thumb]'));
      var counter = gallery.querySelector('[data-gallery-current]');
      var current = 0;

      function setActive(index) {
        current = index;
        thumbs.forEach(function (t, i) {
          t.classList.toggle('is-active', i === index);
          t.setAttribute('aria-current', String(i === index));
        });
        if (counter) counter.textContent = index + 1;
      }

      function goTo(index) {
        if (!slides.length) return;
        index = (index + slides.length) % slides.length;
        var slide = slides[index];
        track.scrollTo({ left: slide.offsetLeft - track.offsetLeft, behavior: reducedMotion ? 'auto' : 'smooth' });
        setActive(index);
      }

      gallery._goTo = goTo;
      gallery._goToImage = function (imageId) {
        var i = slides.findIndex(function (s) { return String(s.getAttribute('data-image-id')) === String(imageId); });
        if (i > -1) goTo(i);
      };

      thumbs.forEach(function (thumb) {
        thumb.addEventListener('click', function () { goTo(parseInt(thumb.getAttribute('data-index'), 10)); });
      });
      var prev = gallery.querySelector('[data-gallery-prev]');
      var next = gallery.querySelector('[data-gallery-next]');
      if (prev) prev.addEventListener('click', function () { goTo(current - 1); });
      if (next) next.addEventListener('click', function () { goTo(current + 1); });

      /* Swiping on mobile: work out which slide is in view */
      var scrollTimer = null;
      track.addEventListener('scroll', function () {
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(function () {
          var left = track.scrollLeft;
          var best = 0;
          var bestDist = Infinity;
          slides.forEach(function (s, i) {
            var d = Math.abs((s.offsetLeft - track.offsetLeft) - left);
            if (d < bestDist) { bestDist = d; best = i; }
          });
          setActive(best);
        }, 60);
      }, { passive: true });

      track.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowRight') { e.preventDefault(); goTo(current + 1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); goTo(current - 1); }
      });
    });
  }

  function initVariantSelector() {
    var form = document.getElementById('ProductFormMain');
    if (!form) return;
    var dataEl = document.querySelector('[data-product-json]');
    if (!dataEl) return;
    var variants;
    try { variants = JSON.parse(dataEl.textContent); } catch (e) { return; }

    var optionSelectors = form.querySelectorAll('[data-option-selector]');
    var variantSelect = form.querySelector('[data-variant-select]');
    var priceEl = document.querySelector('[data-product-price]');
    var compareEl = document.querySelector('[data-compare-price]');
    var submitBtn = form.querySelector('[data-add-to-cart-main]');
    var addLabel = form.querySelector('[data-add-label]');
    var stickyBtn = document.querySelector('[data-sticky-atc-button]');
    var stickyPrice = document.querySelector('[data-sticky-price]');
    var gallery = document.querySelector('[data-product-gallery]');

    function selected() {
      return Array.prototype.map.call(optionSelectors, function (s) { return s.value; });
    }
    function findVariant(sel) {
      return variants.find(function (v) {
        return v.options.every(function (o, i) { return o === sel[i]; });
      });
    }
    function setButtons(available, label) {
      [submitBtn, stickyBtn].forEach(function (btn) {
        if (!btn) return;
        btn.disabled = !available;
        if (available) btn.removeAttribute('data-unavailable'); else btn.setAttribute('data-unavailable', '');
      });
      if (addLabel) addLabel.textContent = label;
      if (stickyBtn) stickyBtn.textContent = label;
    }

    function onChange() {
      var variant = optionSelectors.length ? findVariant(selected()) : variants[0];
      if (!variant) {
        setButtons(false, productStrings.unavailable || 'Unavailable');
        return;
      }
      if (variantSelect) variantSelect.value = variant.id;
      if (priceEl) priceEl.textContent = formatMoney(variant.price);
      if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price);
      if (compareEl) {
        compareEl.textContent = formatMoney(Math.round(variant.price / 0.75));
        compareEl.style.display = variant.price > 0 ? '' : 'none';
      }
      var fallSaleBadge = document.querySelector('[data-fall-sale-badge]');
      if (fallSaleBadge) {
        fallSaleBadge.style.display = variant.price > 0 ? 'inline-block' : 'none';
      }
      if (variant.featured_image && gallery && gallery._goToImage) {
        gallery._goToImage(variant.featured_image.id);
      }
      if (variant.available) setButtons(true, productStrings.addToCart || 'Add to cart');
      else setButtons(false, productStrings.soldOut || 'Sold out');

      /* Keep the URL in sync so a shared link opens the same variant */
      try {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      } catch (e) { /* ignore */ }
    }
    optionSelectors.forEach(function (s) { s.addEventListener('change', onChange); });
  }

  function initQuantity() {
    var input = document.querySelector('[data-quantity-input]');
    var display = document.querySelector('[data-quantity-value]');
    var minus = document.querySelector('[data-quantity-minus]');
    var plus = document.querySelector('[data-quantity-plus]');
    if (!input || !display || !minus || !plus) return;

    function set(q) {
      q = Math.max(1, parseInt(q, 10) || 1);
      input.value = q;
      display.textContent = q;
    }
    plus.addEventListener('click', function () { set((parseInt(input.value, 10) || 1) + 1); });
    minus.addEventListener('click', function () { set((parseInt(input.value, 10) || 1) - 1); });
  }

  /* Sticky add-to-cart bar: visible on mobile once the normal button has scrolled out of view */
  function initStickyAtc() {
    var bar = document.querySelector('[data-sticky-atc]');
    var mainBtn = document.querySelector('[data-add-to-cart-main]');
    if (!bar || !mainBtn || !('IntersectionObserver' in window)) return;
    var footer = document.querySelector('.site-footer');
    var mainVisible = true;
    var footerVisible = false;

    function update() {
      var show = !mainVisible && !footerVisible && mainBtn.getBoundingClientRect().top < 0;
      bar.hidden = !show;
      document.body.classList.toggle('has-sticky-atc', show);
    }
    new IntersectionObserver(function (entries) {
      mainVisible = entries[0].isIntersecting;
      update();
    }).observe(mainBtn);
    if (footer) {
      new IntersectionObserver(function (entries) {
        footerVisible = entries[0].isIntersecting;
        update();
      }).observe(footer);
    }
  }

  /* ---------------------------------------------------------
     6. Accordions, collection sort, scroll animations
     --------------------------------------------------------- */
  function initAccordions() {
    var containers = document.querySelectorAll('[data-accordion]');
    if (!containers.length) return;

    function openItem(item, panel, trigger) {
      item.classList.add('is-open');
      trigger.setAttribute('aria-expanded', 'true');
      panel.style.maxHeight = panel.scrollHeight + 'px';
    }
    function closeItem(item, panel, trigger) {
      item.classList.remove('is-open');
      trigger.setAttribute('aria-expanded', 'false');
      panel.style.maxHeight = '0px';
    }

    containers.forEach(function (container) {
      container.querySelectorAll('[data-accordion-trigger]').forEach(function (trigger) {
        var item = trigger.closest('.accordion__item');
        var panel = item.querySelector('[data-accordion-panel]');
        if (!panel) return;
        if (item.classList.contains('is-open')) panel.style.maxHeight = panel.scrollHeight + 'px';
        trigger.addEventListener('click', function () {
          if (item.classList.contains('is-open')) closeItem(item, panel, trigger);
          else openItem(item, panel, trigger);
        });
      });
    });

    function refresh() {
      document.querySelectorAll('.accordion__item.is-open [data-accordion-panel]').forEach(function (panel) {
        panel.style.maxHeight = panel.scrollHeight + 'px';
      });
    }
    window.addEventListener('resize', refresh);
    window.addEventListener('load', refresh); /* images inside the description change the height */
  }

  function initCollectionSort() {
    var sort = document.querySelector('[data-sort-by]');
    if (!sort) return;
    sort.addEventListener('change', function () {
      var url = new URL(window.location.href);
      url.searchParams.set('sort_by', sort.value);
      url.searchParams.delete('page');
      window.location.href = url.toString();
    });
  }

  function initScrollAnimations() {
    if (reducedMotion || !('IntersectionObserver' in window)) return;
    var targets = document.querySelectorAll('.section-header, .category-tile, .brand-statement__content, .ugc__item, .testimonial');
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12 });
    targets.forEach(function (el) {
      /* Only animate things below the fold, so nothing visible on load flickers */
      if (el.getBoundingClientRect().top > window.innerHeight) {
        el.setAttribute('data-animate', '');
        observer.observe(el);
      }
    });
  }

  /* Snelheid voor browsers zonder 'speculation rules' (bijv. Firefox):
     pagina alvast ophalen als de muis/vinger op een link komt. */
  function initLinkPrefetch() {
    if (HTMLScriptElement.supports && HTMLScriptElement.supports('speculationrules')) return;
    if (navigator.connection && (navigator.connection.saveData || /2g/.test(navigator.connection.effectiveType || ''))) return;
    var done = {};
    var skip = /\/(cart|checkout|checkouts|account|search)(\/|$|\?)/;
    function prefetch(e) {
      var a = e.target.closest && e.target.closest('a[href]');
      if (!a || a.target === '_blank' || a.hasAttribute('data-no-prefetch')) return;
      var url;
      try { url = new URL(a.href, window.location.href); } catch (err) { return; }
      if (url.origin !== window.location.origin || url.search || skip.test(url.pathname)) return;
      if (url.pathname === window.location.pathname || done[url.href]) return;
      done[url.href] = true;
      var link = document.createElement('link');
      link.rel = 'prefetch';
      link.href = url.href;
      document.head.appendChild(link);
    }
    document.addEventListener('mouseover', prefetch, { passive: true });
    document.addEventListener('touchstart', prefetch, { passive: true });
  }

  /* ---------------------------------------------------------
     Init
     --------------------------------------------------------- */
  function init() {
    initHeader();
    initMobileMenu();
    initSearch();
    initCartDrawer();
    initAjaxAddToCart();
    initCartInteractions();
    initProductGallery();
    initVariantSelector();
    initQuantity();
    initStickyAtc();
    initCollectionSort();
    initAccordions();
    initScrollAnimations();
    initLinkPrefetch();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  document.addEventListener('shopify:section:load', function () {
    initProductGallery();
    initAccordions();
  });

  window.theme = theme;
  theme.openCartDrawer = openCartDrawer;
  theme.refreshCartDrawer = refreshCartDrawer;
  theme.formatMoney = formatMoney;
})();
